import type { RequestLeaveItem } from "views/RequestLeave/types"

import { useQuery } from "@apollo/client"
import {
  Alert,
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import Chip from "@mui/material/Chip"
import Paper from "@mui/material/Paper"
import { AttendanceFilter } from "components/AgencySelect"
import {
  CurrentWorkCycleButton,
  DataViewFilterSurface,
  DateRangeFilterControls,
  FilterCollapseButton,
  Flex,
  FlexColumn,
  getDataViewTableStyles,
  GridEmpty,
  LoadingProgress,
  useDataViewTableScroll,
  ViewModeToggle,
} from "components/common"
import { DateTime } from "luxon"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { dataViewPreferencesSelector, updateRequestLeavePreferences } from "reducers/dataViewPreferencesSlice"
import { profileSelector } from "reducers/profileSlice"
import ApproverForm from "views/RequestLeave/ApproverForm"
import RequestLeaveCalendar from "views/RequestLeave/Calendar/RequestLeaveCalendar"
import { REQUEST_ACTION_LABELS, REQUEST_STATUS_COLORS, REQUEST_STATUS_LABELS } from "views/RequestLeave/constants"
import { FIND_REQUEST_LEAVES } from "views/RequestLeave/graphql"
import RequestLeaveReviewActions from "views/RequestLeave/RequestLeaveReviewActions"
import RequestListForm from "views/RequestLeave/RequestListForm"
import { REQUEST_LEAVE_LAYOUT } from "views/RequestLeave/styles"
import { filterRequestLeaves, formatRequestLeavePeriod, groupRequestLeavesByAttendee } from "views/RequestLeave/utils"

const REQUEST_LEAVE_VIEW_OPTIONS = [
  { label: "Danh sách", value: "list" },
  { label: "Lịch", value: "calendar" },
] as const

const RequestLeaveBoard = () => {
  const dispatch = useDispatch()
  const { user } = useSelector(profileSelector)
  const { requestLeave: preferences } = useSelector(dataViewPreferencesSelector)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [actionType, setActionType] = useState<"approve" | "reject">("approve")
  const [selectedLeave, setSelectedLeave] = useState<null | RequestLeaveItem>(null)
  const { onBodyScroll, tableHeadRef } = useDataViewTableScroll()

  const filtersExpanded = preferences.filtersExpanded ?? false
  const attendeeSelected = preferences.attendees
  const showCalendar = preferences.mode === "calendar"
  const startDate = useMemo(() => DateTime.fromISO(preferences.startDate), [preferences.startDate])
  const endDate = useMemo(() => DateTime.fromISO(preferences.endDate), [preferences.endDate])

  const { data, error, loading, refetch } = useQuery(FIND_REQUEST_LEAVES, {
    variables: {
      endDate: endDate.toFormat("yyyy-MM-dd"),
      startDate: startDate.toFormat("yyyy-MM-dd"),
    },
  })

  const filteredLeaves = useMemo(() => {
    return filterRequestLeaves(data?.findRequestLeaves ?? [], {
      attendeeIds: attendeeSelected.map((attendee) => attendee.id),
    })
  }, [attendeeSelected, data])

  const shiftPeriod = (delta: number) => {
    dispatch(
      updateRequestLeavePreferences({
        endDate: endDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
        startDate: startDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
      }),
    )
  }

  const handleOpenConfirmDialog = (leave: RequestLeaveItem, type: "approve" | "reject") => {
    setSelectedLeave(leave)
    setActionType(type)
    setOpenConfirmDialog(true)
  }

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false)
    setSelectedLeave(null)
  }

  const groupedLeaves = useMemo(() => groupRequestLeavesByAttendee(filteredLeaves), [filteredLeaves])

  return (
    <FlexColumn flex={1} gap={2} minHeight={0}>
      <FlexColumn>
        <Flex alignItems="flex-start" className="flex-wrap" gap={2} justifyContent="space-between">
          <Box>
            <Typography variant="h5">Quản lý nghỉ phép</Typography>
            <Typography color="text.secondary" variant="body2">
              Theo dõi, phê duyệt và quản lý các yêu cầu nghỉ của nhân viên.
            </Typography>
          </Box>
          <Flex alignItems="center" className="flex-wrap" gap={1} justifyContent="flex-end">
            <ViewModeToggle
              ariaLabel="Chế độ xem yêu cầu nghỉ phép"
              onChange={(mode) => dispatch(updateRequestLeavePreferences({ mode }))}
              options={REQUEST_LEAVE_VIEW_OPTIONS}
              value={showCalendar ? "calendar" : "list"}
            />
            <FilterCollapseButton
              controlsId="request-leave-filters"
              expanded={filtersExpanded}
              onClick={() => dispatch(updateRequestLeavePreferences({ filtersExpanded: !filtersExpanded }))}
            />
          </Flex>
        </Flex>

        <Collapse id="request-leave-filters" in={filtersExpanded} sx={{ "&.MuiCollapse-hidden": { display: "none" } }}>
          <Box pt={2}>
            <DataViewFilterSurface
              headerAction={
                <CurrentWorkCycleButton
                  endDate={endDate}
                  onChange={({ endDate: nextEndDate, startDate: nextStartDate }) =>
                    dispatch(
                      updateRequestLeavePreferences({
                        endDate: nextEndDate.toFormat("yyyy-MM-dd"),
                        startDate: nextStartDate.toFormat("yyyy-MM-dd"),
                      }),
                    )
                  }
                  startDate={startDate}
                />
              }
              resultLabel={`${filteredLeaves.length} yêu cầu`}
            >
              <Flex alignItems="center" className="flex-wrap" gap={1.5}>
                <DateRangeFilterControls
                  endDate={endDate}
                  onChange={(patch) =>
                    dispatch(
                      updateRequestLeavePreferences({
                        ...(patch.endDate ? { endDate: patch.endDate.toFormat("yyyy-MM-dd") } : {}),
                        ...(patch.startDate ? { startDate: patch.startDate.toFormat("yyyy-MM-dd") } : {}),
                      }),
                    )
                  }
                  onShiftPeriod={shiftPeriod}
                  startDate={startDate}
                />
                <AttendanceFilter
                  attendees={attendeeSelected}
                  onChange={(attendees) => dispatch(updateRequestLeavePreferences({ attendees }))}
                />
              </Flex>
            </DataViewFilterSurface>
          </Box>
        </Collapse>
      </FlexColumn>
      <RequestListForm />
      {error && (
        <Alert
          action={
            <Button color="inherit" onClick={() => void refetch()} size="small">
              Thử lại
            </Button>
          }
          severity="error"
        >
          Không thể tải danh sách yêu cầu.
        </Alert>
      )}
      {!showCalendar && (
        <TableContainer
          className="relative"
          component={Paper}
          sx={{
            borderRadius: 2,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
          variant="outlined"
        >
          <Table
            sx={(theme) =>
              getDataViewTableStyles(theme, {
                gridTemplateColumns: REQUEST_LEAVE_LAYOUT.tableGridColumns,
                minWidth: REQUEST_LEAVE_LAYOUT.tableMinWidth,
              })
            }
          >
            <TableHead ref={tableHeadRef}>
              <TableRow>
                <TableCell align="center">Tên nhân viên</TableCell>
                <TableCell align="center">Thời gian xin nghỉ phép</TableCell>
                <TableCell align="center">Lý do</TableCell>
                <TableCell align="center">Người phê duyệt</TableCell>
                <TableCell align="center">Loại nghỉ phép</TableCell>
                <TableCell align="center">Trạng thái</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody onScroll={onBodyScroll}>
              {loading && (
                <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
                  <TableCell colSpan={7}>
                    <LoadingProgress loading />
                  </TableCell>
                </TableRow>
              )}

              {!error && !loading && groupedLeaves.length === 0 && (
                <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
                  <TableCell>
                    <GridEmpty />
                  </TableCell>
                </TableRow>
              )}

              {groupedLeaves.map((group) =>
                group.leaves.map((leave, index) => (
                  <TableRow hover key={leave.id}>
                    <TableCell align="center" sx={{ bgcolor: index === 0 ? "action.hover" : "transparent" }}>
                      <Typography color="primary.main" fontWeight={700} variant="body2">
                        {index === 0 ? group.attendee.name : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{formatRequestLeavePeriod(leave.startDate, leave.endDate)}</TableCell>
                    <TableCell>{leave.reason}</TableCell>
                    <TableCell align="center">{leave.approver?.name}</TableCell>
                    <TableCell align="center">{REQUEST_ACTION_LABELS[leave.action]}</TableCell>
                    <TableCell align="center">
                      <Chip
                        color={REQUEST_STATUS_COLORS[leave.status]}
                        label={REQUEST_STATUS_LABELS[leave.status]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="right">
                      <RequestLeaveReviewActions
                        leave={leave}
                        onReview={handleOpenConfirmDialog}
                        reviewerDiscordId={user?.attendee?.discordId}
                      />
                    </TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {showCalendar && (
        <Box display="flex" flex={1} minHeight={0} overflow="hidden">
          <RequestLeaveCalendar
            endDate={endDate}
            leaves={filteredLeaves}
            onReview={handleOpenConfirmDialog}
            reviewerDiscordId={user?.attendee?.discordId}
            startDate={startDate}
          />
        </Box>
      )}
      {selectedLeave ? (
        <ApproverForm
          actionType={actionType}
          onClose={handleCloseConfirmDialog}
          open={openConfirmDialog}
          selectedLeave={selectedLeave}
        />
      ) : null}
    </FlexColumn>
  )
}

export default RequestLeaveBoard
